/**
 * @fileoverview Entrypoint for the SMB Listings Launch Flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */
import {hideDelayedProgressDialog, showDelayedProgressDialog} from 'goog:yext.dialog'; // eslint-disable-line

import {ExternalAccountsContextProvider}
  from '/js/yext/externalaccounts/app/listingslaunch/externalaccountscontextprovider';
import setConfirmationDialog from '/src/com/yext/listingslaunchstorm/js/common/hooks/setconfirmationdialog';
import {ListingsLaunchTaskFlow} from '/src/com/yext/listingslaunchstorm/js/components/listingslaunchtaskflow';
import {Success} from '/src/com/yext/listingslaunchstorm/js/components/success';
import {ListingsLaunchContextProvider} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {ListingsLaunchStep} from '/src/com/yext/listingslaunchstorm/js/data/listingslaunchstep';
import {launchListings} from '/src/com/yext/listingslaunchstorm/js/datasource/launchlistings';
import {SmallDialog} from '/ui/components/modal/dialog';
import * as toast from '/ui/components/toast/toast';
import {slowRegisterComponentForJsonData} from '/ui/lib/easyreact';

// this "name":name pattern is needed to make the names work with advanced compilation
export function SmbLaunch({'options': options}) {
  const [hasSaved, setHasSaved] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [submitRequest, setSubmitRequest] = React.useState(undefined);
  const [submitting, setSubmitting] = React.useState(false);

  const shouldWarnBeforeUnload = (!hasSaved && options.entities.length > 0);
  React.useEffect(() => {
    setConfirmationDialog(shouldWarnBeforeUnload);
  }, [shouldWarnBeforeUnload]);

  React.useEffect(() => {
    const abortController = new AbortController();
    if (submitting) {
      const progressTimeoutId = showDelayedProgressDialog(yext.msg('Loading...'));
      launchListings(submitRequest, abortController.signal)
        .then(() => {
          setHasSaved(true);
          setShowConfirm(false);
        })
        .catch(aborted => {
          if (!aborted) {
            toast.showErrorToast(
              <span>
                {yext.msg('There was an error launching your listings.')}
              </span>,
            );
          }
        })
        .finally(() => {
          hideDelayedProgressDialog(progressTimeoutId);
          setSubmitting(false);
          window.scrollTo(0, 0);
        });
    }

    return () => abortController.abort();
  }, [submitRequest, submitting, setSubmitting, setHasSaved, setShowConfirm]);

  const onSave = request => {
    setSubmitRequest(request);
    setShowConfirm(true);
  };

  return (
    <ListingsLaunchContextProvider options={options}>
      <ExternalAccountsContextProvider
        defaultPublisherIdToExternalAccountId={
          new Map([[715, options.gbpConfigurations[0]?.linkedAccountId]])
        }>
        {!hasSaved && <ListingsLaunchTaskFlow onSave={onSave} />}
        {hasSaved && <Success />}

        <SmallDialog
          title={yext.msg('Confirm Submission')}
          callToAction={{
            cancel: {
              type: 'cancel',
              text: yext.msg('Cancel'),
              disabled: submitting,
              pendoId: 'listings-launch-flow-verifying-submission-cancel',
            },
            primary: {
              type: 'active',
              text: yext.msg('Confirm'),
              disabled: submitting,
              callback: () => setSubmitting(true),
              pendoId: 'listings-launch-flow-verifying-submission-confirm',
            },
          }}
          show={showConfirm}
          hideCloseButton={true}
          onHideDialog={() => setShowConfirm(false)}
        >
          {yext.msg('Click Confirm to save and submit your setup. Otherwise, click Cancel to return to the setup and make adjustments')}
        </SmallDialog>
      </ExternalAccountsContextProvider>
    </ListingsLaunchContextProvider>
  );
}

slowRegisterComponentForJsonData('SmbLaunch', SmbLaunch);
